import React from 'react';
import { CheckCircleIcon, DocumentTextIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const CompletionPage = ({ familyMember }) => {
  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-lg border border-secondary-200/50 p-8">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircleIcon className="w-10 h-10 text-primary-600" />
        </div>

        {/* Header */}
        <h1 className="font-serif text-3xl font-semibold text-secondary-800 mb-2">
          Assessment Complete!
        </h1>
        <p className="text-lg text-secondary-600 mb-8">
          Thank you, {familyMember?.firstName}, for completing the family wealth assessment.
        </p>

        {/* What's Next Section */}
        <div className="bg-primary-50/70 backdrop-blur-sm rounded-xl p-6 mb-8 border border-primary-200/50">
          <h2 className="font-serif text-xl font-semibold text-primary-800 mb-4">
            What Happens Next?
          </h2>
          <div className="space-y-4 text-left">
            <div className="flex items-start">
              <DocumentTextIcon className="w-6 h-6 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-primary-800">Analysis & Insights</h3>
                <p className="text-primary-700 text-sm">
                  Your responses will be analyzed along with other family members' assessments to create a comprehensive picture.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <ClockIcon className="w-6 h-6 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-primary-800">Timeline</h3>
                <p className="text-primary-700 text-sm">
                  You can expect to receive your personalized family insights within 5-7 business days.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Details */}
        <div className="bg-secondary-50/70 backdrop-blur-sm rounded-xl p-6 mb-8 border border-secondary-200/50">
          <h3 className="font-semibold text-secondary-800 mb-4">
            Confirmation Details
          </h3>
          <div className="space-y-2 text-sm text-secondary-600">
            <div className="flex justify-between">
              <span>Participant:</span>
              <span className="font-medium text-secondary-800">
                {familyMember?.firstName} {familyMember?.lastName}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Completed:</span>
              <span className="font-medium text-secondary-800">
                {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Status:</span>
              <span className="font-medium text-primary-600">Successfully Submitted</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            className="bg-primary-500 hover:bg-primary-600 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200"
          >
            Return to Homepage
          </Link>
          <button
            onClick={() => window.print()}
            className="border-2 border-primary-300 text-primary-700 hover:bg-primary-50 font-medium px-6 py-3 rounded-lg transition-colors duration-200"
          >
            Print Confirmation
          </button>
        </div>

        {/* Contact Information */}
        <div className="mt-8 pt-6 border-t border-secondary-200">
          <h3 className="font-medium text-secondary-800 mb-2">
            Questions or Concerns?
          </h3>
          <p className="text-secondary-600 text-sm">
            If you have any questions about your assessment or need to make changes,
            please contact your family wealth coach directly.
          </p>
        </div>
      </div>

      {/* Additional Information */}
      <div className="mt-8 bg-accent-50/70 backdrop-blur-sm rounded-xl p-6 border border-accent-200/50">
        <h3 className="font-medium text-accent-800 mb-2">
          Important Notes
        </h3>
        <div className="text-accent-700 text-sm space-y-2 text-left">
          <p>• Your responses are confidential and will only be shared with your designated wealth coach</p>
          <p>• The analysis will consider all family members' responses for a comprehensive view</p>
          <p>• You will receive both individual and family-level insights in your report</p>
          <p>• Your access token is now expired and cannot be used again</p>
        </div>
      </div>
    </div>
  );
};

export default CompletionPage;
